import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';

const ContactContainer = styled.div`
  padding: ${theme.spacing.section} 0;
  background: linear-gradient(to bottom, ${theme.colors.secondary}, ${theme.colors.primary});
  color: ${theme.colors.textLight};
`;

const ContactWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContactHeading = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 64px;
  font-family: ${theme.fonts.heading};
  position: relative;
  text-align: center;
  
  @media screen and (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 48px;
  }
  
  @media screen and (max-width: 480px) {
    font-size: 1.75rem;
  }
  
  &:after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.accent};
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const ContactContent = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const ContactForm = styled.form`
  flex: 1;
  max-width: 600px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  padding: 40px;
  border-radius: ${theme.borderRadius};
  
  @media screen and (max-width: 768px) {
    margin-bottom: 40px;
    max-width: 100%;
    padding: 30px;
  }
  
  @media screen and (max-width: 480px) {
    padding: 20px;
  }
`;

const FormHeading = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: ${theme.borderRadius};
  border: none;
  background: rgba(255, 255, 255, 0.8);
  color: ${theme.colors.textDark};
  font-size: 1rem;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.colors.accent};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border-radius: ${theme.borderRadius};
  border: none;
  background: rgba(255, 255, 255, 0.8);
  color: ${theme.colors.textDark};
  font-size: 1rem;
  min-height: 150px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${theme.colors.accent};
  }
`;

const SubmitButton = styled.button`
  background: ${theme.colors.accent};
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: ${theme.borderRadius};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.accentLight};
    transform: translateY(-3px);
  }
`;

const ContactInfo = styled.div`
  flex: 1;
  padding-left: 50px;
  
  @media screen and (max-width: 768px) {
    padding-left: 0;
  }
`;

const InfoHeading = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 20px;
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 30px;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
`;

const InfoItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-size: 1.1rem;
`;

const InfoIcon = styled.div`
  margin-right: 15px;
  font-size: 1.5rem;
  color: ${theme.colors.accent};
`;

const ContactSection = () => {
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.target;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setStatus('success');
        form.reset();
        setTimeout(() => setStatus(''), 5000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <ContactContainer id="contact">
      <ContactWrapper>
        <ContactHeading>Get In Touch</ContactHeading>
        <ContactContent>
          <ContactForm onSubmit={handleSubmit}>
            <FormHeading>Send Us a Message</FormHeading>
            
            <input type="hidden" name="access_key" value="ab6980f9-1dcf-4faf-a2ef-3649488e2736" />
            <input type="hidden" name="subject" value="New Contact from Rewilding Therapies Website" />
            
            <FormGroup>
              <Label htmlFor="name">Your Name</Label>
              <Input type="text" id="name" name="name" required />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="email">Your Email</Label>
              <Input type="email" id="email" name="email" required />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="phone">Phone Number (Optional)</Label>
              <Input type="tel" id="phone" name="phone" />
            </FormGroup>
            <FormGroup>
              <Label htmlFor="message">Your Message</Label>
              <TextArea id="message" name="message" required></TextArea>
            </FormGroup>
            
            {status === 'sending' && (
              <div style={{ color: theme.colors.accent, marginBottom: '20px', fontWeight: 'bold' }}>
                Sending...
              </div>
            )}
            {status === 'success' && (
              <div style={{ color: theme.colors.accent, marginBottom: '20px', fontWeight: 'bold' }}>
                ✓ Thank you! Your message has been sent successfully.
              </div>
            )}
            {status === 'error' && (
              <div style={{ color: '#ff4444', marginBottom: '20px', fontWeight: 'bold' }}>
                ✗ Something went wrong. Please try again or email us directly.
              </div>
            )}
            
            <SubmitButton type="submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending...' : 'Send Message'}
            </SubmitButton>
          </ContactForm>
          
          <ContactInfo>
            <InfoHeading>Contact Information</InfoHeading>
            <InfoText>
              Ready to start your journey with Rewilding Therapies? We're here to answer 
              any questions you might have about our services or approach.
            </InfoText>
            <InfoList>
              <InfoItem>
                <InfoIcon><FaEnvelope /></InfoIcon>
                hello@rewidingtherapies.co.uk
              </InfoItem>
              <InfoItem>
                <InfoIcon><FaPhone /></InfoIcon>
                07748747530
              </InfoItem>
            </InfoList>
            <InfoText>
              <strong>Nature is waiting for you.</strong> Reach out today to begin your rewilding journey.
            </InfoText>
          </ContactInfo>
        </ContactContent>
      </ContactWrapper>
    </ContactContainer>
  );
};

export default ContactSection;